"use client"

import { useState } from "react"
import { ArrowRight, Database } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

/* -------------------------------------------------------------------------- */
/*  Demo data per platform                                                     */
/* -------------------------------------------------------------------------- */

type Platform = "tiktok" | "linkedin" | "youtube" | "twitter"

const PLATFORM_CONFIG: Record<
  Platform,
  {
    label: string
    placeholder: string
    dataTypes: string[]
    color: string
    data: Record<string, unknown>[]
  }
> = {
  tiktok: {
    label: "TikTok",
    placeholder: "Enter a TikTok username, hashtag, or video URL...\ne.g. @charlidamelio or #foryou",
    dataTypes: ["Posts", "Profiles", "Hashtags"],
    color: "#00f2ea",
    data: [
      {
        id: "7321456789012345678",
        author: "@charlidamelio",
        description: "New dance trend alert! Who can keep up? #foryou #dance",
        likes: 2_400_000,
        comments: 18_500,
        shares: 45_200,
        views: 12_800_000,
        created: "2026-06-04T14:30:00Z",
      },
      {
        id: "7321456789012345679",
        author: "@khaby.lame",
        description: "When people overcomplicate simple things...",
        likes: 5_100_000,
        comments: 32_000,
        shares: 89_000,
        views: 28_000_000,
        created: "2026-06-03T09:15:00Z",
      },
      {
        id: "7321456789012345680",
        author: "@bellapoarch",
        description: "POV: your pet judges your cooking skills #pets #cooking",
        likes: 1_800_000,
        comments: 12_400,
        shares: 28_700,
        views: 9_200_000,
        created: "2026-06-02T18:45:00Z",
      },
    ],
  },
  linkedin: {
    label: "LinkedIn",
    placeholder: "Enter a LinkedIn profile URL or company page...\ne.g. linkedin.com/in/satyanadella",
    dataTypes: ["Profiles", "Posts", "Companies"],
    color: "#0a66c2",
    data: [
      {
        name: "Satya Nadella",
        headline: "Chairman and CEO at Microsoft",
        followers: 11_200_000,
        connections: "500+",
        location: "Redmond, Washington",
        experience: [
          { role: "Chairman and CEO", company: "Microsoft", years: "2014-present" },
        ],
      },
      {
        name: "Sheryl Sandberg",
        headline: "Former COO at Meta | Author of Lean In",
        followers: 8_500_000,
        connections: "500+",
        location: "Menlo Park, California",
        experience: [
          { role: "Board Member", company: "Meta Platforms", years: "2022-present" },
        ],
      },
      {
        name: "Reid Hoffman",
        headline: "Co-Founder, LinkedIn | Partner at Greylock",
        followers: 4_200_000,
        connections: "500+",
        location: "Palo Alto, California",
        experience: [
          { role: "Partner", company: "Greylock Partners", years: "2009-present" },
        ],
      },
    ],
  },
  youtube: {
    label: "YouTube",
    placeholder: "Enter a YouTube channel URL, video URL, or search query...\ne.g. @MrBeast or youtube.com/watch?v=...",
    dataTypes: ["Posts", "Profiles", "Hashtags"],
    color: "#ff0000",
    data: [
      {
        videoId: "dQw4w9WgXcQ",
        title: "I Built 100 Houses And Gave Them Away!",
        channel: "@MrBeast",
        views: 142_000_000,
        likes: 4_800_000,
        comments: 285_000,
        duration: "PT18M24S",
        published: "2026-05-28T16:00:00Z",
      },
      {
        videoId: "abc123xyz",
        title: "The Future of AI in Marketing — Full Breakdown",
        channel: "@GaryVee",
        views: 3_200_000,
        likes: 185_000,
        comments: 12_400,
        duration: "PT12M08S",
        published: "2026-06-01T14:00:00Z",
      },
      {
        videoId: "def456uvw",
        title: "How I Grew to 1M Subscribers in 6 Months",
        channel: "@AliAbdaal",
        views: 8_900_000,
        likes: 420_000,
        comments: 18_200,
        duration: "PT22M15S",
        published: "2026-05-30T10:00:00Z",
      },
    ],
  },
  twitter: {
    label: "X / Twitter",
    placeholder: "Enter a X/Twitter username, hashtag, or tweet URL...\ne.g. @elonmusk or #AI",
    dataTypes: ["Posts", "Profiles", "Hashtags"],
    color: "#000000",
    data: [
      {
        tweetId: "1798765432109876543",
        author: "@elonmusk",
        text: "The future of AI is going to be wild. Stay tuned.",
        likes: 320_000,
        retweets: 45_000,
        replies: 28_000,
        views: 18_500_000,
        created: "2026-06-04T22:10:00Z",
      },
      {
        tweetId: "1798765432109876544",
        author: "@sama",
        text: "GPT-5 will change how every company operates. Not just tech companies.",
        likes: 89_000,
        retweets: 12_500,
        replies: 5_400,
        views: 4_200_000,
        created: "2026-06-03T15:30:00Z",
      },
      {
        tweetId: "1798765432109876545",
        author: "@lexfridman",
        text: "New podcast dropping tomorrow with a guest who will surprise everyone. #AI #podcast",
        likes: 45_000,
        retweets: 8_200,
        replies: 3_100,
        views: 2_800_000,
        created: "2026-06-02T20:00:00Z",
      },
    ],
  },
}

const MAX_RESULTS_OPTIONS = ["10", "25", "50", "100"]

/* -------------------------------------------------------------------------- */
/*  JSON preview renderer                                                      */
/* -------------------------------------------------------------------------- */

function JsonPreview({ data, color }: { data: Record<string, unknown>[]; color: string }) {
  const json = JSON.stringify(data, null, 2)
  // Limit display length for readability
  const lines = json.split("\n")
  const displayLines = lines.slice(0, 42)
  const truncated = lines.length > 42

  return (
    <div className="relative h-full overflow-hidden rounded-lg bg-[#0d1117] font-mono text-[11px] leading-[1.6] sm:text-xs">
      {/* Top bar */}
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
        <div className="flex gap-1.5">
          <span className="size-2.5 rounded-full bg-[#ff5f57]" />
          <span className="size-2.5 rounded-full bg-[#febc2e]" />
          <span className="size-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="text-[10px] text-white/40">response.json</span>
        <Badge
          className="ml-auto border-0 text-[9px]"
          style={{ backgroundColor: `${color}20`, color }}
        >
          {data.length} results
        </Badge>
      </div>

      {/* Code */}
      <div className="overflow-y-auto p-4" style={{ maxHeight: "380px" }}>
        <pre className="text-white/80">
          {displayLines.map((line, i) => (
            <div key={i} className="flex">
              <span className="mr-4 inline-block w-5 shrink-0 select-none text-right text-white/20">
                {i + 1}
              </span>
              <span>{colorizeJson(line, color)}</span>
            </div>
          ))}
          {truncated && (
            <div className="flex">
              <span className="mr-4 inline-block w-5 shrink-0 select-none text-right text-white/20">
                ...
              </span>
              <span className="text-white/40">{`// ${lines.length - 42} more lines`}</span>
            </div>
          )}
        </pre>
      </div>
    </div>
  )
}

/**
 * Naive JSON syntax coloring for the preview — keeps it lightweight.
 */
function colorizeJson(line: string, accent: string) {
  // Color keys
  const colored = line
    .replace(/"([^"]+)":/g, `<k>"$1"</k>:`)
    .replace(/"([^"]+)"/g, `<s>"$1"</s>`)
    .replace(/\b(\d[\d_,]*)\b/g, `<n>$1</n>`)

  const parts = colored.split(/(<\/?[ksn]>)/g)
  let mode: "k" | "s" | "n" | null = null

  return (
    <>
      {parts.map((part, i) => {
        if (part === "<k>") { mode = "k"; return null }
        if (part === "</k>") { mode = null; return null }
        if (part === "<s>") { mode = "s"; return null }
        if (part === "</s>") { mode = null; return null }
        if (part === "<n>") { mode = "n"; return null }
        if (part === "</n>") { mode = null; return null }
        if (!part) return null

        const style =
          mode === "k"
            ? { color: "#79c0ff" }
            : mode === "s"
              ? { color: "#a5d6ff" }
              : mode === "n"
                ? { color: accent }
                : undefined

        return (
          <span key={i} style={style}>
            {part}
          </span>
        )
      })}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*  Main studio component                                                      */
/* -------------------------------------------------------------------------- */

export function ApifyStudio({
  generateHref,
}: {
  generateHref: string
}) {
  const [platform, setPlatform] = useState<Platform>("tiktok")
  const [dataType, setDataType] = useState("Posts")
  const [maxResults, setMaxResults] = useState("25")

  const config = PLATFORM_CONFIG[platform]

  return (
    <div className="w-full rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6 lg:p-8">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        {/* Left: Controls */}
        <div className="flex flex-col justify-between gap-4 sm:gap-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
              Social Media Scraper
            </p>
            <h3 className="text-lg font-semibold text-foreground sm:text-xl">
              Data Extraction Studio
            </h3>
          </div>

          {/* Platform selector */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Platform
            </label>
            <Select
              value={platform}
              onValueChange={(v) => {
                setPlatform(v as Platform)
                setDataType(PLATFORM_CONFIG[v as Platform].dataTypes[0])
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="twitter">X / Twitter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Target input */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Target
            </label>
            <Textarea
              className="h-20 resize-none text-sm"
              placeholder={config.placeholder}
              readOnly
            />
          </div>

          {/* Data type selector */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Data Type
            </label>
            <div className="flex flex-wrap gap-1.5">
              {config.dataTypes.map((dt) => (
                <button
                  key={dt}
                  onClick={() => setDataType(dt)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    dataType === dt
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {dt}
                </button>
              ))}
            </div>
          </div>

          {/* Max results */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Max Results
            </label>
            <Select value={maxResults} onValueChange={setMaxResults}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MAX_RESULTS_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt} results
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* CTA */}
          <Button className="w-full rounded-full" size="lg" asChild>
            <a href={generateHref} target="_blank" rel="noopener noreferrer">
              <Database className="size-4" />
              Scrape with Soku AI
              <ArrowRight className="size-4" />
            </a>
          </Button>
        </div>

        {/* Right: JSON Preview */}
        <div className="min-h-[420px]">
          <JsonPreview data={config.data} color={config.color} />
        </div>
      </div>
    </div>
  )
}
